import {
  AuthContext,
  type ConfirmSignUpInput,
  type ResendSignUpHandler,
  type SignUpInput,
} from "hooks/authProvider/authProvider";
import type React from "react";
import { useCallback, useContext, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SignUpPage } from "routes/SignUpPage/SignUpPage";
import { toastClearAll, toastError, toastPromise } from "utils/toast-utils";

const SignUpPageContainer: React.VFC = () => {
  const [searchParams] = useSearchParams();
  const emailFromInvitation = searchParams.get("email") || "";

  const [signUpInput] = useState<SignUpInput>({
    familyName: "",
    givenName: "",
    familyNameKana: "",
    givenNameKana: "",
    email: emailFromInvitation,
    password: "",
  });
  const [isConfirming, setIsConfirming] = useState(false);
  const { signUpStatus, signUp, confirmSignUp, resendSignUp } =
    useContext(AuthContext);

  const navigate = useNavigate();

  const onSignUp: SubmitHandler<SignUpInput> = useCallback(
    async (input: SignUpInput) => {
      try {
        await signUp(input);
        toastClearAll();
      } catch (error: unknown) {
        const err = error as Error;
        toastError(err.message);
      }
    },
    [signUp],
  );

  const onConfirmSignUp: SubmitHandler<Omit<ConfirmSignUpInput, "email">> =
    useCallback(
      async (input: Omit<ConfirmSignUpInput, "email">) => {
        setIsConfirming(true);
        try {
          await confirmSignUp({
            email: signUpStatus.email,
            authCode: input.authCode,
          });
          toastClearAll();
          navigate("/");
        } catch (error: unknown) {
          const err = error as Error;
          toastError(err.message);
        } finally {
          setIsConfirming(false);
        }
      },
      [signUpStatus.email, navigate, confirmSignUp],
    );

  const onResendSignUp: ResendSignUpHandler = useCallback(async () => {
    try {
      await toastPromise(resendSignUp(), {
        pending: "認証コード再送中...",
        success: "認証コードを再送しました",
        error: "認証コードの再送に失敗しました",
      });
    } catch (error: unknown) {
      const err = error as Error;
      toastError(err.message);
    }
  }, [resendSignUp]);

  return (
    <SignUpPage
      signUpInput={signUpInput}
      signUpStatus={signUpStatus}
      onSignUp={onSignUp}
      onConfirmSignUp={onConfirmSignUp}
      onResendSignUp={onResendSignUp}
      isConfirming={isConfirming}
      defaultEmail={emailFromInvitation}
    />
  );
};

export default SignUpPageContainer;
